<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import {
  useMapGetter,
  useStore,
  useStoreGetters,
} from 'dashboard/composables/store';
import Auth from 'dashboard/api/auth';
import WorkforceAPI from 'dashboard/api/workforce';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import reportsAPI from 'dashboard/api/reports';
import format from 'date-fns/format';
import fromUnixTime from 'date-fns/fromUnixTime';
import IosCheckbox from 'dashboard/components-next/checkbox/IosCheckbox.vue';
import { getUserPermissions } from 'dashboard/helper/permissionsHelper';
import {
  AGENT_PERMISSIONS,
  AGENT_PROFILE_PERMISSIONS,
  AGENT_STATUS_PERMISSIONS,
  ASSIGNMENT_POLICY_PERMISSIONS,
  ATTENDANCE_PERMISSIONS,
} from 'dashboard/constants/permissions.js';

const AVATAR_COLORS = [
  '#4285f4',
  '#ea4335',
  '#34a853',
  '#fbbc04',
  '#9334e6',
  '#00bcd4',
  '#ff7043',
];

const WEEKDAY_LABELS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];
const WEEKDAY_API_MAP = [1, 2, 3, 4, 5, 6, 0];

const store = useStore();
const getters = useStoreGetters();
const currentUser = useMapGetter('getCurrentUser');
const currentAccountId = useMapGetter('getCurrentAccountId');

const currentPermissions = computed(() =>
  getUserPermissions(currentUser.value, currentAccountId.value)
);

const hasPermission = permission => {
  return (
    currentPermissions.value.includes('administrator') ||
    currentPermissions.value.includes(permission)
  );
};

const canManageAgents = computed(() => hasPermission(AGENT_PERMISSIONS));
const canManageAgentProfiles = computed(
  () =>
    canManageAgents.value || hasPermission(AGENT_PROFILE_PERMISSIONS)
);
const canManageAgentStatuses = computed(
  () =>
    canManageAgents.value || hasPermission(AGENT_STATUS_PERMISSIONS)
);
const canManageWorkingHours = computed(
  () =>
    canManageAgents.value ||
    hasPermission(ASSIGNMENT_POLICY_PERMISSIONS) ||
    hasPermission(ATTENDANCE_PERMISSIONS)
);
const canOpenAgentProfileDrawer = computed(
  () => canManageAgentProfiles.value || canManageAgents.value
);

const agentPriorityRank = agent => {
  if (agent?.availability_status === 'offline') return 2;
  return agent?.accepts_chat === false || agent?.availability_status === 'busy'
    ? 1
    : 0;
};

const sortAgents = agents =>
  [...agents].sort((left, right) => {
    const rankDiff = agentPriorityRank(left) - agentPriorityRank(right);
    if (rankDiff !== 0) return rankDiff;

    const leftName = String(left?.name || left?.email || '').toLowerCase();
    const rightName = String(right?.name || right?.email || '').toLowerCase();
    return leftName.localeCompare(rightName);
  });

const searchQuery = ref('');
const allAgents = computed(() => getters['agents/getAgents'].value);
const agentsUiFlags = computed(() => getters['agents/getUIFlags'].value);
const isLoading = computed(() => agentsUiFlags.value?.isFetching);
const currentUserId = computed(() => getters.getCurrentUserID.value);
const customRoles = useMapGetter('customRole/getCustomRoles');

const filteredAgents = computed(() => {
  const list = allAgents.value || [];
  const q = String(searchQuery.value || '')
    .trim()
    .toLowerCase();
  const filtered = !q
    ? list
    : list.filter(
        a =>
          String(a.name || '')
            .toLowerCase()
            .includes(q) ||
          String(a.email || '')
            .toLowerCase()
            .includes(q)
      );
  return sortAgents(filtered);
});

// ─── Live monitor metrics ─────────────────────────────────
const liveMetricsByAgentId = ref({});
const liveMetricsFetchedAt = ref(Date.now());
const liveMetricsRefreshTimer = ref(null);
const liveMetricsTickTimer = ref(null);
const metricsNow = ref(Date.now());
const liveMetricsDebounceTimer = ref(null);
const lastActivityTrackedAtMs = ref(0);
const activityTrackDebounceTimer = ref(null);

// ─── Agent activity (report timeline) ─────────────────────
const agentActivityByUserId = ref({});
const agentActivityUI = ref({
  fetchedAt: null,
  from: null,
  to: null,
});
const agentActivityRefreshTimer = ref(null);
const agentActivityMidnightTimer = ref(null);
const agentActivityDebounceTimer = ref(null);

const startOfLocalDayEpoch = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return Math.floor(d.getTime() / 1000);
};
const endOfLocalDayEpoch = () => startOfLocalDayEpoch() + 24 * 60 * 60 - 1;
const activityPeriodSeconds = 24 * 60 * 60;
const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const millisecondsUntilNextLocalMidnight = () => {
  const now = new Date();
  const nextMidnight = new Date(now);
  nextMidnight.setHours(24, 0, 0, 0);
  return Math.max(nextMidnight.getTime() - now.getTime(), 1000);
};

const fetchAgentActivityToday = async () => {
  try {
    const userIds = (allAgents.value || [])
      .map(a => Number(a?.id))
      .filter(Boolean);
    if (!userIds.length) return;

    const from = startOfLocalDayEpoch();
    const to = endOfLocalDayEpoch();

    const response = await reportsAPI.getAgentActivity({
      from,
      to,
      userIds,
    });

    const rows = response?.data?.agents || [];
    const map = {};
    rows.forEach(row => {
      const id = String(row?.user_id || '');
      if (!id) return;
      map[id] = row;
    });
    agentActivityByUserId.value = map;
    agentActivityUI.value = {
      fetchedAt: new Date(),
      from,
      to,
    };
  } catch {
    // keep last
  }
};

const scheduleAgentActivityRefresh = () => {
  if (agentActivityDebounceTimer.value) {
    clearTimeout(agentActivityDebounceTimer.value);
    agentActivityDebounceTimer.value = null;
  }
  agentActivityDebounceTimer.value = setTimeout(() => {
    fetchAgentActivityToday();
    agentActivityDebounceTimer.value = null;
  }, 1500);
};

const startAgentActivityRefresh = () => {
  if (agentActivityRefreshTimer.value) {
    clearInterval(agentActivityRefreshTimer.value);
    agentActivityRefreshTimer.value = null;
  }
  if (agentActivityMidnightTimer.value) {
    clearTimeout(agentActivityMidnightTimer.value);
    agentActivityMidnightTimer.value = null;
  }
  agentActivityRefreshTimer.value = setInterval(fetchAgentActivityToday, 60000);
  agentActivityMidnightTimer.value = setTimeout(async () => {
    await fetchAgentActivityToday();
    startAgentActivityRefresh();
  }, millisecondsUntilNextLocalMidnight());
};
const stopAgentActivityRefresh = () => {
  if (agentActivityRefreshTimer.value) {
    clearInterval(agentActivityRefreshTimer.value);
    agentActivityRefreshTimer.value = null;
  }
  if (agentActivityMidnightTimer.value) {
    clearTimeout(agentActivityMidnightTimer.value);
    agentActivityMidnightTimer.value = null;
  }
  if (agentActivityDebounceTimer.value) {
    clearTimeout(agentActivityDebounceTimer.value);
    agentActivityDebounceTimer.value = null;
  }
};

// ─── Agent activity timeline helpers ───────────────────────
const AA_STATUS_ACCEPTING = 'accepting';
const AA_STATUS_NOT_ACCEPTING = 'not_accepting';
const AA_STATUS_UPCOMING = 'upcoming';

const aaHourTicks = computed(() =>
  Array.from({ length: 25 }, (_, hour) => hour)
);
const aaHoveredSegment = ref(null);

const aaStatusLegend = [
  {
    key: AA_STATUS_ACCEPTING,
    className: 'aa-legend-dot--accepting',
    label: 'Active',
  },
  {
    key: AA_STATUS_NOT_ACCEPTING,
    className: 'aa-legend-dot--not-accepting',
    label: 'Inactive',
  },
  {
    key: AA_STATUS_UPCOMING,
    className: 'aa-legend-dot--upcoming',
    label: 'Upcoming',
  },
];

const aaNormalizeStatus = value => {
  if (value === AA_STATUS_ACCEPTING) return AA_STATUS_ACCEPTING;
  return AA_STATUS_NOT_ACCEPTING;
};

const aaStatusText = status => {
  if (status === AA_STATUS_ACCEPTING) return 'Active';
  if (status === AA_STATUS_UPCOMING) return 'Upcoming';
  return 'Inactive';
};

const aaStatusSegmentClass = status => {
  if (status === AA_STATUS_ACCEPTING) return 'aa-segment--accepting';
  if (status === AA_STATUS_UPCOMING) return 'aa-segment--upcoming';
  return 'aa-segment--not-accepting';
};

const aaFormatDurationBadge = totalSeconds => {
  const seconds = Math.max(Math.round(Number(totalSeconds || 0)), 0);
  if (!seconds) return '-';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainderSeconds = seconds % 60;
  if (hours > 0)
    return `${String(hours).padStart(2, '0')}h ${String(minutes).padStart(2, '0')}m`;
  if (minutes > 0)
    return `${minutes}m ${String(remainderSeconds).padStart(2, '0')}s`;
  return `${remainderSeconds}s`;
};

const aaFormatDurationDetailed = totalSeconds => {
  const seconds = Math.max(Math.round(Number(totalSeconds || 0)), 0);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${String(hours).padStart(2, '0')}h ${String(minutes).padStart(2, '0')}m`;
};

const aaSegmentTooltip = segment => {
  const startAt = Number(segment.from || 0);
  const endAt = Number(segment.to || 0);
  if (!startAt || !endAt)
    return `${aaStatusText(segment.status)}: ${aaFormatDurationDetailed(segment.duration_seconds)}`;
  const fromLabel = format(fromUnixTime(startAt), 'hh:mm aaa').toLowerCase();
  const toLabel = format(fromUnixTime(endAt), 'hh:mm aaa').toLowerCase();
  return `${fromLabel} - ${toLabel} | ${aaStatusText(segment.status)}: ${aaFormatDurationDetailed(segment.duration_seconds)}`;
};

const aaSegmentTimeRangeLabel = segment => {
  const startAt = Number(segment.from || 0);
  const endAt = Number(segment.to || 0);
  if (!startAt || !endAt) return '';
  const fromLabel = format(fromUnixTime(startAt), 'hh:mm aaa').toLowerCase();
  const toLabel = format(fromUnixTime(endAt), 'hh:mm aaa').toLowerCase();
  return `${fromLabel} - ${toLabel}`;
};

const aaOnSegmentEnter = ({ agentId, segment, index }) => {
  const start = Number(segment.start_percent || 0);
  const width = Number(segment.width_percent || 0);
  const center = start + width / 2;
  aaHoveredSegment.value = {
    agentId,
    index,
    segment,
    centerPercent: Number.isFinite(center) ? center : 0,
  };
};

const aaOnSegmentLeave = ({ agentId, index }) => {
  if (!aaHoveredSegment.value) return;
  if (
    aaHoveredSegment.value.agentId === agentId &&
    aaHoveredSegment.value.index === index
  ) {
    aaHoveredSegment.value = null;
  }
};

const normalizeAgentActivityStatus = aaNormalizeStatus;

const fallbackSegmentsFromHours = (statuses, periodStart) => {
  const normalized = (statuses || []).map(normalizeAgentActivityStatus);
  if (!normalized.length) return [];

  const buckets = [];
  let startHour = 0;
  let current = normalized[0];

  for (let hour = 1; hour <= normalized.length; hour += 1) {
    const status = normalized[hour];
    if (status === current) continue;

    const endHour = hour;
    const hoursSpan = endHour - startHour;
    const from = periodStart + startHour * 3600;
    const to = periodStart + endHour * 3600;
    const durationSeconds = Math.max(hoursSpan * 3600, 0);

    buckets.push({
      status: current,
      from,
      to,
      duration_seconds: durationSeconds,
      // Keep identical to AgentActivityReports.vue: map hours to 24h width.
      start_percent: (startHour / 24) * 100,
      width_percent: (hoursSpan / 24) * 100,
    });

    current = status;
    startHour = hour;
  }

  return buckets;
};

const activityTimelineSegments = agent => {
  const row = agentActivityByUserId.value[String(agent?.id || '')];
  if (!row) return [];

  const periodStart = Number(
    agentActivityUI.value.from || startOfLocalDayEpoch()
  );
  const periodEnd = periodStart + activityPeriodSeconds;
  const nowEpoch = Math.floor(metricsNow.value / 1000);
  const elapsedEnd = clamp(nowEpoch, periodStart, periodEnd);

  const rawSegments = (row.timeline_segments || [])
    .map(segment => {
      const status = normalizeAgentActivityStatus(segment.status);
      const rawFrom = Number(segment.from);
      const rawTo = Number(segment.to);
      if (Number.isFinite(rawFrom) && Number.isFinite(rawTo) && rawTo > rawFrom) {
        return {
          ...segment,
          status,
          from: rawFrom,
          to: rawTo,
          duration_seconds: Math.max(
            Number(segment.duration_seconds || rawTo - rawFrom),
            0
          ),
        };
      }

      const startPercent = Number(segment.start_percent || 0);
      const widthPercent = Number(segment.width_percent || 0);
      const startAt =
        periodStart + (startPercent / 100) * activityPeriodSeconds;
      const endAt = startAt + (widthPercent / 100) * activityPeriodSeconds;
      return {
        ...segment,
        status,
        from: startAt,
        to: endAt,
        duration_seconds: Math.max(Math.round(endAt - startAt), 0),
      };
    })
    .filter(segment => Number(segment.to) > Number(segment.from))
    .sort((a, b) => Number(a.from) - Number(b.from));

  const baseSegments = rawSegments.length
    ? rawSegments
    : fallbackSegmentsFromHours(row.hourly_statuses || [], periodStart);

  const segments = [];
  let cursor = periodStart;

  baseSegments.forEach(segment => {
    const segFrom = clamp(Number(segment.from || 0), periodStart, periodEnd);
    const segTo = clamp(Number(segment.to || 0), periodStart, periodEnd);
    if (segTo <= segFrom) return;
    if (segFrom >= elapsedEnd) return;

    if (segFrom > cursor) {
      const gapTo = Math.min(segFrom, elapsedEnd);
      if (gapTo > cursor) {
        segments.push({
          status: AA_STATUS_NOT_ACCEPTING,
          from: cursor,
          to: gapTo,
          duration_seconds: gapTo - cursor,
          start_percent: ((cursor - periodStart) / activityPeriodSeconds) * 100,
          width_percent: ((gapTo - cursor) / activityPeriodSeconds) * 100,
        });
      }
    }

    const visibleFrom = Math.max(segFrom, cursor);
    const visibleTo = Math.min(segTo, elapsedEnd);
    if (visibleTo > visibleFrom) {
      segments.push({
        ...segment,
        status: normalizeAgentActivityStatus(segment.status),
        from: visibleFrom,
        to: visibleTo,
        duration_seconds: visibleTo - visibleFrom,
        start_percent:
          ((visibleFrom - periodStart) / activityPeriodSeconds) * 100,
        width_percent:
          ((visibleTo - visibleFrom) / activityPeriodSeconds) * 100,
      });
      cursor = visibleTo;
    }
  });

  if (cursor < elapsedEnd) {
    segments.push({
      status: AA_STATUS_NOT_ACCEPTING,
      from: cursor,
      to: elapsedEnd,
      duration_seconds: elapsedEnd - cursor,
      start_percent: ((cursor - periodStart) / activityPeriodSeconds) * 100,
      width_percent: ((elapsedEnd - cursor) / activityPeriodSeconds) * 100,
    });
  }

  if (elapsedEnd < periodEnd) {
    segments.push({
      status: AA_STATUS_UPCOMING,
      from: elapsedEnd,
      to: periodEnd,
      duration_seconds: periodEnd - elapsedEnd,
      start_percent: ((elapsedEnd - periodStart) / activityPeriodSeconds) * 100,
      width_percent: ((periodEnd - elapsedEnd) / activityPeriodSeconds) * 100,
    });
  }

  return segments.filter(segment => Number(segment.width_percent) > 0);
};

const aaLegendDurationSeconds = (agent, status) =>
  activityTimelineSegments(agent)
    .filter(segment => segment.status === status)
    .reduce(
      (total, segment) => total + Math.max(Number(segment.duration_seconds || 0), 0),
      0
    );

const fetchLiveMetrics = async () => {
  try {
    const response = await WorkforceAPI.getLiveStatus();
    const liveAgents = response?.agents || response?.data?.agents || [];
    const nextMap = {};
    liveAgents.forEach(la => {
      const key = String(la.user_id || la.id || '');
      if (!key) return;
      nextMap[key] = {
        time_since_last_reply_seconds:
          la.time_since_last_reply_seconds ??
          la.time_since_reply_seconds ??
          la.time_since_last_reply ??
          null,
        time_since_last_activity_seconds:
          la.time_since_last_activity_seconds ??
          la.time_since_activity_seconds ??
          la.time_since_last_activity ??
          null,
        pending_conversations_count:
          la.pending_conversations_count ?? la.pending_count ?? null,
      };
    });
    liveMetricsByAgentId.value = nextMap;
    liveMetricsFetchedAt.value = Date.now();
    metricsNow.value = Date.now();
  } catch {
    /* keep last */
  }
};

const scheduleLiveMetricsRefresh = () => {
  if (liveMetricsDebounceTimer.value) {
    clearTimeout(liveMetricsDebounceTimer.value);
    liveMetricsDebounceTimer.value = null;
  }
  liveMetricsDebounceTimer.value = setTimeout(() => {
    fetchLiveMetrics();
    liveMetricsDebounceTimer.value = null;
  }, 1200);
};

const patchAgentLiveNow = (agentId, patch = {}) => {
  const key = String(agentId || '');
  if (!key) return;
  const existing = liveMetricsByAgentId.value[key] || {};
  liveMetricsByAgentId.value = {
    ...liveMetricsByAgentId.value,
    [key]: {
      time_since_last_reply_seconds:
        patch.time_since_last_reply_seconds ??
        existing.time_since_last_reply_seconds,
      time_since_last_activity_seconds:
        patch.time_since_last_activity_seconds ??
        existing.time_since_last_activity_seconds,
      pending_conversations_count:
        patch.pending_conversations_count ??
        existing.pending_conversations_count,
    },
  };
  liveMetricsFetchedAt.value = Date.now();
  metricsNow.value = Date.now();
};

const sendTrackActivity = async () => {
  // Throttle to avoid spamming the endpoint.
  const now = Date.now();
  const minIntervalMs = 15000;
  if (now - lastActivityTrackedAtMs.value < minIntervalMs) return;
  lastActivityTrackedAtMs.value = now;
  try {
    await WorkforceAPI.trackActivity();
  } catch {
    // keep UI optimistic
  }
};

const scheduleTrackActivity = () => {
  if (document?.hidden) return;

  // Optimistically update current user's last activity immediately.
  if (currentUserId.value) {
    patchAgentLiveNow(currentUserId.value, {
      time_since_last_activity_seconds: 0,
    });
  }

  // Debounce network call.
  if (activityTrackDebounceTimer.value) {
    clearTimeout(activityTrackDebounceTimer.value);
    activityTrackDebounceTimer.value = null;
  }
  activityTrackDebounceTimer.value = setTimeout(() => {
    sendTrackActivity();
    // Pull fresh metrics soon so other fields stay accurate.
    scheduleLiveMetricsRefresh();
    activityTrackDebounceTimer.value = null;
  }, 800);
};

const startLiveTimers = () => {
  stopLiveTimers();
  liveMetricsTickTimer.value = setInterval(() => {
    metricsNow.value = Date.now();
  }, 1000);
  liveMetricsRefreshTimer.value = setInterval(fetchLiveMetrics, 30000);
};
const stopLiveTimers = () => {
  if (liveMetricsTickTimer.value) clearInterval(liveMetricsTickTimer.value);
  if (liveMetricsRefreshTimer.value)
    clearInterval(liveMetricsRefreshTimer.value);
  liveMetricsTickTimer.value = null;
  liveMetricsRefreshTimer.value = null;
  if (liveMetricsDebounceTimer.value)
    clearTimeout(liveMetricsDebounceTimer.value);
  liveMetricsDebounceTimer.value = null;
  if (activityTrackDebounceTimer.value)
    clearTimeout(activityTrackDebounceTimer.value);
  activityTrackDebounceTimer.value = null;
};

const agentLive = a => liveMetricsByAgentId.value[String(a?.id || '')] || null;
const inflate = raw => {
  if (raw === null || raw === undefined) return null;
  const base = Number(raw);
  if (Number.isNaN(base)) return null;
  return Math.max(
    0,
    base + Math.floor((metricsNow.value - liveMetricsFetchedAt.value) / 1000)
  );
};
const fmtDur = raw => {
  const s = inflate(raw);
  if (s === null) return '—';
  if (s < 5) return 'Just now';
  if (s < 60) return `${Math.floor(s)}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${Math.floor(s % 60)}s`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
};
const agentLastReply = a => fmtDur(agentLive(a)?.time_since_last_reply_seconds);
const agentLastActivity = a =>
  fmtDur(agentLive(a)?.time_since_last_activity_seconds);
const agentQueue = a =>
  Number(
    agentLive(a)?.pending_conversations_count ??
      a?.pending_conversations_count ??
      0
  ) || 0;

const nowSeconds = computed(() => Math.floor(metricsNow.value / 1000));
const sameLocalDay = (epochSecondsA, epochSecondsB) => {
  if (!epochSecondsA || !epochSecondsB) return false;
  return (
    new Date(epochSecondsA * 1000).toDateString() ===
    new Date(epochSecondsB * 1000).toDateString()
  );
};
const agentLastReplyAtEpoch = a => {
  const s = inflate(agentLive(a)?.time_since_last_reply_seconds);
  if (s === null) return null;
  return Math.max(0, nowSeconds.value - s);
};
const agentReplyActivityToday = a => {
  const lastReplyAt = agentLastReplyAtEpoch(a);
  const isToday = lastReplyAt && sameLocalDay(lastReplyAt, nowSeconds.value);
  if (!lastReplyAt || !isToday) {
    return {
      tone: 'none',
      label: 'No reply today',
      title: 'No replies from this agent today',
    };
  }
  const d = new Date(lastReplyAt * 1000);
  const timeLabel = d.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
  return {
    tone: 'ok',
    label: timeLabel,
    title: `Last reply today at ${d.toLocaleString()}`,
  };
};

// ─── Status helpers ───────────────────────────────────────
const lcStatus = a => {
  if (a.availability_status === 'offline') return 'offline';
  if (a.accepts_chat === false || a.availability_status === 'busy')
    return 'not_accepting';
  if (a.availability_status === 'online') return 'accepting';
  return 'offline';
};
const lcStatusLabel = a => {
  const s = lcStatus(a);
  if (s === 'accepting') return 'Accepting chats';
  if (s === 'not_accepting') return 'Not accepting chats';
  return 'Offline';
};
const lcStatusDot = a => {
  const s = lcStatus(a);
  if (s === 'accepting') return '#38c172';
  if (s === 'not_accepting') return '#e3342f';
  return '#9ca3af';
};

// ─── Role helpers ─────────────────────────────────────────
const findCustomRole = a =>
  (customRoles.value || []).find(r => r.id === a.custom_role_id);
const getAgentRoleName = a => {
  if (a?.type === 'SuperAdmin') return 'Super admin';
  if (a.custom_role_id) return findCustomRole(a)?.name || 'Custom role';
  if (a.role === 'administrator') return 'Owner';
  if (a.role)
    return String(a.role)
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  return 'Agent';
};
const isOwner = a => a.role === 'administrator';
const roleOptions = computed(() => {
  const defaults = [
    { value: 'administrator', label: 'Owner / Administrator' },
    { value: 'agent', label: 'Agent' },
  ];
  const custom = (customRoles.value || []).map(r => ({
    value: `custom_${r.id}`,
    label: r.name,
    permissions: r.permissions || [],
  }));
  return [...defaults, ...custom];
});

const avatarColor = a =>
  AVATAR_COLORS[Number(a?.id || 0) % AVATAR_COLORS.length];
const avatarInitial = a =>
  String(a?.name || a?.email || '?')
    .slice(0, 1)
    .toUpperCase();

// ─── Status dropdown ──────────────────────────────────────
const activeStatusDropdown = ref(null);
const statusUpdating = ref({});
const openStatusDropdown = (a, e) => {
  e.stopPropagation();
  activeStatusDropdown.value =
    activeStatusDropdown.value === a.id ? null : a.id;
};
const closeStatusDropdown = () => {
  activeStatusDropdown.value = null;
};
const setAgentStatus = async (a, target) => {
  closeStatusDropdown();
  statusUpdating.value[a.id] = true;
  try {
    const payload = { id: a.id };
    if (target === 'online') {
      payload.availability = 'online';
      payload.accepts_chat = true;
    } else if (target === 'not_accepting' || target === 'busy') {
      payload.availability = 'online';
      payload.accepts_chat = false;
    } else {
      payload.availability = 'offline';
      payload.accepts_chat = false;
    }
    await store.dispatch('agents/update', payload);
    useAlert(`Status updated to ${target}`);
  } catch {
    useAlert('Failed to update status');
  } finally {
    delete statusUpdating.value[a.id];
  }
};

const logoutSelf = async () => {
  closeStatusDropdown();
  try {
    await Auth.logout();
  } finally {
    window.location.assign('/app/login');
  }
};

const onStatusLogoutClick = async agent => {
  // Only the current user can be logged out from the client.
  // For other agents, fall back to setting them offline.
  if (String(agent?.id) === String(currentUserId.value)) {
    await logoutSelf();
    return;
  }
  await setAgentStatus(agent, 'offline');
};

// ─── Invite Agent modal ───────────────────────────────────
const showInviteModal = ref(false);
const inviteForm = ref({ name: '', email: '', roleValue: 'agent' });
const inviteSubmitting = ref(false);
const openInviteModal = () => {
  inviteForm.value = { name: '', email: '', roleValue: 'agent' };
  showInviteModal.value = true;
};
const submitInvite = async () => {
  const { name, email, roleValue } = inviteForm.value;
  if (!name.trim() || !email.trim()) {
    useAlert('Name and email are required');
    return;
  }
  inviteSubmitting.value = true;
  const payload = { name: name.trim(), email: email.trim() };
  const rv = String(roleValue || 'agent');
  if (rv.startsWith('custom_')) {
    payload.role = 'agent';
    payload.custom_role_id = Number(rv.replace('custom_', ''));
  } else {
    payload.role = rv;
  }
  try {
    await store.dispatch('agents/create', payload);
    useAlert('Agent invited successfully');
    showInviteModal.value = false;
    store.dispatch('agents/get');
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Failed to invite agent');
  } finally {
    inviteSubmitting.value = false;
  }
};

// ─── Delete agent ─────────────────────────────────────────
const showDeleteModal = ref(false);
const agentToDelete = ref(null);
const deleteLoading = ref(false);
const confirmDeleteAgent = async () => {
  if (!agentToDelete.value) return;
  const delId = agentToDelete.value.id;
  deleteLoading.value = true;
  try {
    await store.dispatch('agents/delete', delId);
    useAlert('Agent deleted');
    showDeleteModal.value = false;
    if (showDetails.value && String(selectedAgentId.value) === String(delId))
      closeDrawer();
    agentToDelete.value = null;
  } catch {
    useAlert('Failed to delete agent');
  } finally {
    deleteLoading.value = false;
  }
};

// ─── Profile drawer ───────────────────────────────────────
const showDetails = ref(false);
const selectedAgentId = ref(null);
const selectedAgent = computed(
  () =>
    (allAgents.value || []).find(
      a => String(a.id) === String(selectedAgentId.value)
    ) || null
);
const openDrawer = a => {
  if (!canOpenAgentProfileDrawer.value) return;
  selectedAgentId.value = String(a.id);
  showDetails.value = true;
  resetEditForm(a);
  if (canManageWorkingHours.value) {
    loadAgentSchedules(a.id);
  } else {
    agentSchedules.value = [];
  }
};
const closeDrawer = () => {
  showDetails.value = false;
};

const handleRowClick = agent => {
  if (!canOpenAgentProfileDrawer.value) return;
  openDrawer(agent);
};

const editForm = ref({
  name: '',
  displayName: '',
  selectedRoleValue: 'agent',
  availability: 'online',
  chatLimit: 3,
  jobTitle: '',
  timeFormat: '12',
  agentStatusAfterLogin: 'accept',
  emailDailySummary: true,
  emailWeeklySummary: true,
});
const editSaving = ref(false);

const resetEditForm = a => {
  if (!a) return;
  editForm.value = {
    name: a.name || '',
    displayName: a.display_name || '',
    selectedRoleValue: a.custom_role_id
      ? `custom_${a.custom_role_id}`
      : a.role || 'agent',
    availability: a.availability_status || 'online',
    chatLimit: Number(a?.chat_limit || 3),
    jobTitle: a?.job_title || '',
    timeFormat: '12',
    agentStatusAfterLogin: 'accept',
    emailDailySummary: true,
    emailWeeklySummary: true,
  };
};

watch(selectedAgent, a => {
  if (a) resetEditForm(a);
});

const saveProfile = async () => {
  if (
    !selectedAgent.value ||
    (!canManageAgentProfiles.value &&
      !canManageAgentStatuses.value &&
      !canManageAgents.value)
  )
    return;
  editSaving.value = true;
  const rv = String(editForm.value.selectedRoleValue || 'agent');
  const payload = { id: selectedAgent.value.id };

  if (canManageAgentProfiles.value || canManageAgents.value) {
    payload.name = editForm.value.name;
    payload.display_name = editForm.value.displayName;
    payload.chat_limit = Math.max(1, Number(editForm.value.chatLimit || 1));
    payload.job_title = editForm.value.jobTitle;
  }

  if (canManageAgentStatuses.value || canManageAgents.value) {
    payload.availability = editForm.value.availability;
  }

  if (canManageAgents.value) {
    if (rv.startsWith('custom_')) {
      payload.role = 'agent';
      payload.custom_role_id = Number(rv.replace('custom_', ''));
    } else {
      payload.role = rv;
      payload.custom_role_id = null;
    }
  }
  try {
    await store.dispatch('agents/update', payload);
    useAlert('Agent profile updated');
    store.dispatch('agents/get');
  } catch {
    useAlert('Failed to update profile');
  } finally {
    editSaving.value = false;
  }
};

const resetPassword = async a => {
  if (!a?.email) return;
  try {
    await Auth.resetPassword({ email: a.email });
    useAlert('Password reset email sent');
  } catch {
    useAlert('Failed to send password reset email');
  }
};

// ─── Working hours (per-agent schedules) ──────────────────
const agentSchedules = ref([]);
const agentSchedulesLoading = ref(false);
const agentSchedulesSaving = ref(false);

const loadAgentSchedules = async userId => {
  agentSchedulesLoading.value = true;
  try {
    const res = await WorkforceAPI.getSchedules({ user_id: userId });
    agentSchedules.value = res?.data || [];
  } catch {
    agentSchedules.value = [];
  } finally {
    agentSchedulesLoading.value = false;
  }
};

const agentScheduleByWeekday = computed(() => {
  const map = {};
  (agentSchedules.value || []).forEach(s => {
    const wd = s.weekday ?? s.day_of_week;
    if (wd !== null && wd !== undefined) map[wd] = s;
  });
  return map;
});

const weekdayDisplay = idx => {
  const apiDay = WEEKDAY_API_MAP[idx];
  const sched = agentScheduleByWeekday.value[apiDay];
  if (!sched || !sched.is_active)
    return { label: WEEKDAY_LABELS[idx], active: false, range: 'No schedule' };
  const fmt = mins => {
    if (mins === null || mins === undefined) return '--';
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };
  return {
    label: WEEKDAY_LABELS[idx],
    active: true,
    range: `${fmt(sched.start_time)} – ${fmt(sched.end_time)}`,
  };
};

const totalScheduledHours = computed(() => {
  let totalMins = 0;
  (agentSchedules.value || []).forEach(s => {
    if (!s.is_active) return;
    const start = s.start_time ?? 0;
    const end = s.end_time ?? 0;
    if (end > start) totalMins += end - start;
  });
  return `${Math.floor(totalMins / 60)}h`;
});

const toggleScheduleDay = async idx => {
  if (!selectedAgent.value) return;
  const apiDay = WEEKDAY_API_MAP[idx];
  const existing = agentScheduleByWeekday.value[apiDay];
  agentSchedulesSaving.value = true;
  try {
    if (existing) {
      await WorkforceAPI.updateSchedule(existing.id, {
        is_active: !existing.is_active,
      });
    } else {
      await WorkforceAPI.createSchedule({
        user_id: selectedAgent.value.id,
        weekday: apiDay,
        start_time: 540,
        end_time: 1080,
        is_active: true,
      });
    }
    await loadAgentSchedules(selectedAgent.value.id);
  } catch {
    useAlert('Failed to update schedule');
  } finally {
    agentSchedulesSaving.value = false;
  }
};

// ─── Accordion state ──────────────────────────────────────
const openAccordions = ref({
  details: true,
  status: true,
  workingHours: true,
  role: false,
  time: false,
  email: false,
  twofa: false,
});
const toggleAccordion = k => {
  openAccordions.value[k] = !openAccordions.value[k];
};

const handleDocumentClick = () => closeStatusDropdown();
const userActivityThrottleMs = 3000;
const lastUserActivityFiredAt = ref(0);
const handleUserActivity = () => {
  const now = Date.now();
  if (now - lastUserActivityFiredAt.value < userActivityThrottleMs) return;
  lastUserActivityFiredAt.value = now;
  scheduleTrackActivity();
};
const handleWsEvent = payload => {
  const { event, data } = payload || {};
  if (event !== 'message.created' && event !== 'message.updated') return;

  // Only treat outgoing user messages as an agent reply.
  const messageType = Number(data?.message_type);
  const senderType = String(data?.sender_type || '');
  const senderId = data?.sender?.id ?? data?.user_id ?? data?.sender_id;

  // message_type: 1 = outgoing; sender_type: 'User' = agent
  if (messageType === 1 && senderType === 'User' && senderId) {
    patchAgentLiveNow(senderId, {
      time_since_last_reply_seconds: 0,
      time_since_last_activity_seconds: 0,
    });
    scheduleLiveMetricsRefresh();
    scheduleAgentActivityRefresh();
  }
};

onMounted(async () => {
  document.addEventListener('click', handleDocumentClick);
  emitter.on(BUS_EVENTS.WEBSOCKET_EVENT_RECEIVED, handleWsEvent);
  // Update "Last activity" immediately on user interaction
  window.addEventListener('pointerdown', handleUserActivity, { passive: true });
  window.addEventListener('keydown', handleUserActivity, { passive: true });
  window.addEventListener('scroll', handleUserActivity, { passive: true });
  document.addEventListener('visibilitychange', handleUserActivity, {
    passive: true,
  });
  await Promise.allSettled([
    store.dispatch('agents/get'),
    store.dispatch('customRole/getCustomRole'),
  ]);
  await fetchLiveMetrics();
  startLiveTimers();
  await fetchAgentActivityToday();
  startAgentActivityRefresh();
  scheduleTrackActivity();
});
onUnmounted(() => {
  document.removeEventListener('click', handleDocumentClick);
  emitter.off(BUS_EVENTS.WEBSOCKET_EVENT_RECEIVED, handleWsEvent);
  window.removeEventListener('pointerdown', handleUserActivity);
  window.removeEventListener('keydown', handleUserActivity);
  window.removeEventListener('scroll', handleUserActivity);
  document.removeEventListener('visibilitychange', handleUserActivity);
  stopLiveTimers();
  stopAgentActivityRefresh();
});
</script>

<template>
  <div class="tm" @click="closeStatusDropdown">
    <div class="tm__header">
      <button class="tm__icon-btn" type="button" aria-label="Toggle navigation">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          width="20"
          height="20"
        >
          <path
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M9 4v16M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z"
          />
        </svg>
      </button>
      <h4 class="tm__header-title">Team</h4>
      <div class="tm__header-spacer" />
      <button class="tm__invite-link" type="button">
        Invite settings
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          width="14"
          height="14"
        >
          <path
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6m4-3h6v6m-11 5L21 3"
          />
        </svg>
      </button>
    </div>

    <div class="tm__toolbar">
      <div class="tm__search relative w-full sm:w-72" data-testid="search-input">
        <span
          class="tm__contact-search-icon absolute i-ph-magnifying-glass-bold size-4 top-1/2 -translate-y-1/2 left-3 text-n-slate-11 pointer-events-none"
        />
        <input
          v-model="searchQuery"
          role="searchbox"
          class="tm__contact-search-input w-full h-10 pl-10 pr-3 text-sm outline-none border border-n-weak rounded-lg bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12 placeholder:text-n-slate-11"
          type="search"
          placeholder="Search agent"
        />
      </div>
      <div class="tm__toolbar-actions">
        <span class="tm__agent-count">{{ filteredAgents.length }} agents</span>
        <button
          v-if="canManageAgents"
          class="tm__btn tm__btn--primary"
          type="button"
          @click.stop="openInviteModal"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            width="16"
            height="16"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 5v14m-7-7h14"
            />
          </svg>
          Invite agents
        </button>
      </div>
    </div>

    <div class="tm__body">
      <div class="tm__table-wrap">
        <div v-if="isLoading" class="tm__state-center">
          <div class="tm__spinner" role="status" />
        </div>
        <div
          v-else-if="!filteredAgents.length"
          class="tm__state-center tm__state-empty"
        >
          No agents found
        </div>
        <div v-else class="tm__table-scroll">
          <div class="tm__section-label">
            Active ({{ filteredAgents.length }})
          </div>
          <div class="tm__row tm__row--head" role="row">
            <div class="tm__cell tm__cell--check" role="columnheader">
              <span class="tm__checkbox-placeholder" />
            </div>
            <div class="tm__cell tm__cell--name" role="columnheader">Name</div>
            <div class="tm__cell tm__cell--metric" role="columnheader">
              Last reply
            </div>
            <div class="tm__cell tm__cell--activity" role="columnheader">
              Agent activity
            </div>
            <div class="tm__cell tm__cell--metric" role="columnheader">
              Last activity
            </div>
            <div class="tm__cell tm__cell--metric" role="columnheader">
              Queue
            </div>
            <div class="tm__cell tm__cell--role" role="columnheader">Role</div>
            <div class="tm__cell tm__cell--status" role="columnheader">
              Status
            </div>
            <div class="tm__cell tm__cell--more" role="columnheader" />
          </div>
          <div
            v-if="canManageAgents"
            class="tm__row tm__row--add"
            role="row"
            @click.stop="openInviteModal"
          >
            <div class="tm__cell tm__cell--check" role="cell" />
            <div class="tm__cell tm__cell--name" role="cell">
              <div class="tm__add-btn">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 5v14m-7-7h14"
                  />
                </svg>
              </div>
              <span class="tm__add-label">Add new agent</span>
            </div>
            <div class="tm__cell tm__cell--metric" role="cell" />
            <div class="tm__cell tm__cell--activity" role="cell" />
            <div class="tm__cell tm__cell--metric" role="cell" />
            <div class="tm__cell tm__cell--metric" role="cell" />
            <div class="tm__cell tm__cell--role" role="cell" />
            <div class="tm__cell tm__cell--status" role="cell" />
            <div class="tm__cell tm__cell--more" role="cell" />
          </div>
          <div
            v-for="agent in filteredAgents"
            :key="agent.id"
            class="tm__row tm__row--data"
            role="row"
            :class="{
              'tm__row--selected':
                showDetails && String(selectedAgentId) === String(agent.id),
            }"
            @click="handleRowClick(agent)"
          >
            <div class="tm__cell tm__cell--check" role="cell">
              <span class="tm__checkbox-placeholder" />
            </div>
            <div class="tm__cell tm__cell--name" role="cell">
              <div class="tm__agent-avatar-wrap">
                <div
                  class="tm__avatar"
                  :style="{ backgroundColor: avatarColor(agent) }"
                >
                  <img
                    v-if="agent.thumbnail"
                    :src="agent.thumbnail"
                    :alt="agent.name"
                    class="tm__avatar-img"
                  />
                  <span v-else>{{ avatarInitial(agent) }}</span>
                </div>
                <span
                  class="tm__avatar-status-dot"
                  :style="{ backgroundColor: lcStatusDot(agent) }"
                />
              </div>
              <div class="tm__name-col">
                <span class="tm__name">{{ agent.name
                  }}<span
                    v-if="agent.id === currentUserId"
                    class="tm__you-badge"
                    >&nbsp;(You)</span></span>
                <span class="tm__sub">{{ agent.email }}</span>
              </div>
            </div>
            <div class="tm__cell tm__cell--metric" role="cell">
              {{ agentLastReply(agent) }}
            </div>
            <div class="tm__cell tm__cell--activity" role="cell" @click.stop>
              <div class="aa-row-activity">
                <div class="aa-activity-chart">
                  <div class="aa-hour-ticks" aria-hidden="true">
                    <span
                      v-for="hour in aaHourTicks"
                      :key="`tick-${agent.id}-${hour}`"
                      class="aa-hour-tick"
                      :class="{ 'is-major': hour % 4 === 0 }"
                    />
                  </div>
                  <div class="aa-track">
                    <div class="aa-track-inner">
                      <div
                        v-for="(segment, index) in activityTimelineSegments(
                          agent
                        )"
                        :key="`${agent.id}-seg-${index}`"
                        class="aa-segment"
                        :class="aaStatusSegmentClass(segment.status)"
                        :style="{
                          left: `${segment.start_percent}%`,
                          width: `${segment.width_percent}%`,
                        }"
                        :title="aaSegmentTooltip(segment)"
                        @mouseenter="
                          aaOnSegmentEnter({
                            agentId: agent.id,
                            segment,
                            index,
                          })
                        "
                        @mouseleave="
                          aaOnSegmentLeave({ agentId: agent.id, index })
                        "
                      />
                    </div>
                    <div
                      v-if="aaHoveredSegment?.agentId === agent.id"
                      class="aa-segment-tooltip"
                      :style="{ left: `${aaHoveredSegment.centerPercent}%` }"
                    >
                      <div class="aa-segment-tooltip__time">
                        {{ aaSegmentTimeRangeLabel(aaHoveredSegment.segment) }}
                      </div>
                      <div class="aa-segment-tooltip__line">
                        {{ aaStatusText(aaHoveredSegment.segment.status) }}:
                        <strong>{{
                          aaFormatDurationDetailed(
                            aaHoveredSegment.segment.duration_seconds
                          )
                        }}</strong>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="aa-legend-row">
                  <span
                    v-for="legend in aaStatusLegend"
                    :key="`${agent.id}-${legend.key}`"
                    class="aa-legend-item"
                  >
                    <span class="aa-legend-dot" :class="legend.className" />
                    <span>{{ legend.label }}:</span>
                    <strong>{{
                      aaFormatDurationBadge(aaLegendDurationSeconds(agent, legend.key))
                    }}</strong>
                  </span>
                </div>
              </div>
            </div>
            <div class="tm__cell tm__cell--metric" role="cell">
              {{ agentLastActivity(agent) }}
            </div>
            <div class="tm__cell tm__cell--metric" role="cell">
              <span class="tm__queue-pill">{{ agentQueue(agent) }}</span>
            </div>
            <div class="tm__cell tm__cell--role" role="cell">
              <span
                class="tm__role-badge"
                :class="{ 'tm__role-badge--owner': isOwner(agent) }"
                >{{ getAgentRoleName(agent) }}</span>
            </div>
            <div class="tm__cell tm__cell--status" role="cell">
              <div class="tm__status-wrap">
                <template v-if="canManageAgentStatuses || canManageAgents">
                  <button
                    class="tm__status-btn"
                    type="button"
                    :disabled="!!statusUpdating[agent.id]"
                    @click.stop="openStatusDropdown(agent, $event)"
                  >
                    <span
                      class="tm__dot"
                      :style="{ backgroundColor: lcStatusDot(agent) }"
                    />
                    {{ lcStatusLabel(agent) }}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      width="14"
                      height="14"
                      class="tm__status-chevron"
                      :class="{
                        'tm__status-chevron--open':
                          activeStatusDropdown === agent.id,
                      }"
                    >
                      <path
                        stroke="currentColor"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="m6 9 6 6 6-6"
                      />
                    </svg>
                  </button>
                  <Transition name="tm-popup">
                    <div
                      v-if="activeStatusDropdown === agent.id"
                      class="tm__status-popup"
                      @click.stop
                    >
                      <ul class="tm__status-list" role="menu">
                        <li role="none">
                          <button
                            class="tm__status-item"
                            type="button"
                            @click.stop="setAgentStatus(agent, 'online')"
                          >
                            <span class="tm__sdot tm__sdot--green" />Accepting
                            chats
                          </button>
                        </li>
                        <li role="none" class="tm__status-item-divider">
                          <button
                            class="tm__status-item"
                            type="button"
                            @click.stop="setAgentStatus(agent, 'not_accepting')"
                          >
                            <span class="tm__sdot tm__sdot--red" />Not accepting
                            chats
                          </button>
                        </li>
                        <li role="none">
                          <button
                            class="tm__status-item"
                            type="button"
                            @click.stop="onStatusLogoutClick(agent)"
                          >
                            <span class="tm__sdot tm__sdot--gray" />Log out
                          </button>
                        </li>
                      </ul>
                    </div>
                  </Transition>
                </template>
                <div
                  v-else
                  class="tm__status-btn"
                  style="cursor: default; pointer-events: none"
                >
                  <span
                    class="tm__dot"
                    :style="{ backgroundColor: lcStatusDot(agent) }"
                  />
                  {{ lcStatusLabel(agent) }}
                </div>
              </div>
            </div>
            <div class="tm__cell tm__cell--more" role="cell">
              <button
                v-if="canOpenAgentProfileDrawer"
                class="tm__more-btn"
                type="button"
                @click.stop="openDrawer(agent)"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M5 12h.01M12 12h.01M19 12h.01"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Profile Drawer with backdrop -->
    <Transition name="tm-drawer-overlay">
      <div
        v-if="showDetails && selectedAgent"
        class="tm__drawer-overlay"
        @click="closeDrawer"
      >
        <div class="tm__drawer" @click.stop>
          <div class="tm__drawer-head">
            <button class="tm__back-btn" type="button" @click="closeDrawer">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="16"
                height="16"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="m15 18-6-6 6-6"
                />
              </svg>
              Back
            </button>
            <span class="tm__drawer-title">Profile</span>
            <button class="tm__drawer-close" type="button" @click="closeDrawer">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="m6 6 12 12m0-12L6 18"
                />
              </svg>
            </button>
          </div>
          <div class="tm__drawer-body">
            <div class="tm__profile-hero">
              <div class="tm__profile-avatar-wrap">
                <div
                  class="tm__avatar tm__avatar--profile"
                  :style="{ backgroundColor: avatarColor(selectedAgent) }"
                >
                  <img
                    v-if="selectedAgent.thumbnail"
                    :src="selectedAgent.thumbnail"
                    :alt="selectedAgent.name"
                    class="tm__avatar-img"
                  />
                  <span v-else>{{ avatarInitial(selectedAgent) }}</span>
                </div>
                <span
                  class="tm__profile-status-dot"
                  :style="{ backgroundColor: lcStatusDot(selectedAgent) }"
                />
              </div>
              <div class="tm__profile-name-col">
                <span
                  class="tm__role-badge tm__role-badge--hero"
                  :class="{ 'tm__role-badge--owner': isOwner(selectedAgent) }"
                  >{{ getAgentRoleName(selectedAgent) }}</span>
                <p class="tm__profile-name">{{ selectedAgent.name }}</p>
                <p class="tm__profile-sub">
                  {{ editForm.jobTitle || selectedAgent.email }}
                </p>
              </div>
            </div>

            <!-- Details -->
            <div class="tm__dc tm__dc--divider">
              <div class="tm__dc-heading" @click="toggleAccordion('details')">
                <h5 class="tm__dc-title">Details</h5>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  class="tm__dc-chevron"
                  :class="{ 'tm__dc-chevron--open': openAccordions.details }"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="m9 6 6 6-6 6"
                  />
                </svg>
              </div>
              <div v-if="openAccordions.details" class="tm__dc-content">
                <div class="tm__field">
                  <label class="tm__flabel">Full name</label>
                  <input
                    v-model="editForm.name"
                    type="text"
                    class="tm__finput"
                    placeholder="Agent name"
                  />
                </div>
                <div class="tm__field">
                  <label class="tm__flabel">Display name</label>
                  <input
                    v-model="editForm.displayName"
                    type="text"
                    class="tm__finput"
                    placeholder="Name shown in live chat"
                  />
                  <p class="tm__fhint">Shown to customers in live chat.</p>
                </div>
                <div class="tm__field">
                  <div class="tm__flabel-row">
                    <label class="tm__flabel">Email</label><button
                      v-if="canManageAgents"
                      class="tm__change-pw"
                      type="button"
                      @click="resetPassword(selectedAgent)"
                    >
                      Change password
                    </button>
                  </div>
                  <div class="tm__finput-wrap">
                    <input
                      :value="selectedAgent.email"
                      type="email"
                      class="tm__finput tm__finput--locked"
                      readonly
                    />
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      width="16"
                      height="16"
                      class="tm__flock"
                    >
                      <path
                        stroke="currentColor"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M17 11H7a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-5a2 2 0 0 0-2-2zm-5-4a3 3 0 0 1 3 3H9a3 3 0 0 1 3-3z"
                      />
                    </svg>
                  </div>
                </div>
                <div class="tm__field">
                  <label class="tm__flabel">Job title</label>
                  <input
                    v-model="editForm.jobTitle"
                    type="text"
                    class="tm__finput"
                    placeholder="e.g. Product Expert"
                  />
                </div>
                <div class="tm__field">
                  <div class="tm__flabel-row">
                    <label class="tm__flabel">Chat limit</label>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      width="14"
                      height="14"
                      style="color: rgba(19, 19, 23, 0.45); cursor: help"
                      title="Maximum concurrent chats"
                    >
                      <path
                        stroke="currentColor"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 8h.01M11 12h1v4h1M3 12a9 9 0 1 0 18 0 9 9 0 0 0-18 0"
                      />
                    </svg>
                  </div>
                  <div class="tm__clwrap">
                    <div class="tm__clgroup">
                      <input
                        v-model.number="editForm.chatLimit"
                        type="number"
                        min="1"
                        max="99"
                        class="tm__clinput"
                      />
                      <div class="tm__clarrows">
                        <button
                          type="button"
                          class="tm__clarrow"
                          @click="
                            editForm.chatLimit = Math.min(
                              99,
                              (editForm.chatLimit || 0) + 1
                            )
                          "
                        >
                          ▲
                        </button>
                        <button
                          type="button"
                          class="tm__clarrow"
                          @click="
                            editForm.chatLimit = Math.max(
                              1,
                              (editForm.chatLimit || 0) - 1
                            )
                          "
                        >
                          ▼
                        </button>
                      </div>
                    </div>
                  </div>
                  <p class="tm__fhint">3 is optimal for rookies</p>
                </div>
                <div v-if="canManageAgents" class="tm__field">
                  <label class="tm__flabel">Role</label>
                  <select
                    v-model="editForm.selectedRoleValue"
                    class="tm__fselect"
                  >
                    <option
                      v-for="r in roleOptions"
                      :key="r.value"
                      :value="r.value"
                    >
                      {{ r.label }}
                    </option>
                  </select>
                </div>
                <button
                  v-if="
                    canManageAgentProfiles ||
                    canManageAgentStatuses ||
                    canManageAgents
                  "
                  class="tm__btn tm__btn--primary tm__btn--full"
                  type="button"
                  :disabled="editSaving"
                  @click="saveProfile"
                >
                  {{ editSaving ? 'Saving…' : 'Save changes' }}
                </button>
              </div>
            </div>

            <!-- Agent status after logging in -->
            <div
              v-if="canManageAgentStatuses || canManageAgents"
              class="tm__dc tm__dc--divider"
            >
              <div class="tm__dc-heading" @click="toggleAccordion('status')">
                <h5 class="tm__dc-title">Agent status after logging in</h5>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  class="tm__dc-chevron"
                  :class="{ 'tm__dc-chevron--open': openAccordions.status }"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="m9 6 6 6-6 6"
                  />
                </svg>
              </div>
              <div v-if="openAccordions.status" class="tm__dc-content">
                <label class="tm__radio-label"><input
                    v-model="editForm.agentStatusAfterLogin"
                    type="radio"
                    value="accept"
                    class="tm__radio"
                  /><span>Accept chats</span></label>
                <label class="tm__radio-label"><input
                    v-model="editForm.agentStatusAfterLogin"
                    type="radio"
                    value="reject"
                    class="tm__radio"
                  /><span>Don't accept chats</span></label>
                <label class="tm__radio-label tm__radio-label--multiline">
                  <input
                    v-model="editForm.agentStatusAfterLogin"
                    type="radio"
                    value="hours"
                    class="tm__radio"
                  />
                  <span>According to working hours<small class="tm__radio-sub">Switch agent status based on working hours
                      schedule.</small></span>
                </label>
              </div>
            </div>

            <!-- Working hours -->
            <div v-if="canManageWorkingHours" class="tm__dc tm__dc--divider">
              <div
                class="tm__dc-heading"
                @click="toggleAccordion('workingHours')"
              >
                <h5 class="tm__dc-title">Working hours</h5>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  class="tm__dc-chevron"
                  :class="{
                    'tm__dc-chevron--open': openAccordions.workingHours,
                  }"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="m9 6 6 6-6 6"
                  />
                </svg>
              </div>
              <div v-if="openAccordions.workingHours" class="tm__dc-content">
                <div v-if="agentSchedulesLoading" class="tm__dc-empty-note">
                  Loading schedules…
                </div>
                <template v-else>
                  <div class="tm__wh-header">
                    <div class="tm__wh-tz">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        width="14"
                        height="14"
                      >
                        <circle
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          stroke-width="1.5"
                        />
                        <path
                          stroke="currentColor"
                          stroke-width="1.5"
                          d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10A15.3 15.3 0 0 1 12 2z"
                        />
                      </svg>
                      Timezone: Always based on agents location
                    </div>
                    <span class="tm__wh-total">Total: {{ totalScheduledHours }}</span>
                  </div>
                  <div class="tm__wh-days">
                    <div
                      v-for="(_, idx) in WEEKDAY_LABELS"
                      :key="idx"
                      class="tm__wh-day"
                      :class="{
                        'tm__wh-day--active': weekdayDisplay(idx).active,
                      }"
                    >
                      <label class="tm__wh-day-check">
                        <IosCheckbox
                          :model-value="weekdayDisplay(idx).active"
                          :disabled="agentSchedulesSaving"
                          :size="22"
                          aria-label="Toggle working day"
                          @update:model-value="() => toggleScheduleDay(idx)"
                        />
                      </label>
                      <span class="tm__wh-day-label">{{
                        weekdayDisplay(idx).label
                      }}</span>
                      <span class="tm__wh-day-range">{{
                        weekdayDisplay(idx).range
                      }}</span>
                    </div>
                  </div>
                </template>
              </div>
            </div>

            <!-- Role and permissions -->
            <div class="tm__dc tm__dc--divider">
              <div class="tm__dc-heading" @click="toggleAccordion('role')">
                <h5 class="tm__dc-title">Role and permissions</h5>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  class="tm__dc-chevron"
                  :class="{ 'tm__dc-chevron--open': openAccordions.role }"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="m9 6 6 6-6 6"
                  />
                </svg>
              </div>
              <div v-if="openAccordions.role" class="tm__dc-content">
                <p class="tm__section-desc">
                  <template v-if="isOwner(selectedAgent)">
                    This agent is the owner and has full access.
                  </template>
                  <template v-else>
                    This agent has standard agent permissions.
                  </template>
                </p>
              </div>
            </div>

            <!-- Time format -->
            <div class="tm__dc tm__dc--divider">
              <div class="tm__dc-heading" @click="toggleAccordion('time')">
                <h5 class="tm__dc-title">Time format</h5>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  class="tm__dc-chevron"
                  :class="{ 'tm__dc-chevron--open': openAccordions.time }"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="m9 6 6 6-6 6"
                  />
                </svg>
              </div>
              <div v-if="openAccordions.time" class="tm__dc-content">
                <label class="tm__radio-label"><input
                    v-model="editForm.timeFormat"
                    type="radio"
                    value="12"
                    class="tm__radio"
                  /><span><strong>12-hour</strong> (09:23:00 pm)</span></label>
                <label class="tm__radio-label"><input
                    v-model="editForm.timeFormat"
                    type="radio"
                    value="24"
                    class="tm__radio"
                  /><span><strong>24-hour</strong> (21:23:00)</span></label>
              </div>
            </div>

            <!-- Email updates -->
            <div class="tm__dc tm__dc--divider">
              <div class="tm__dc-heading" @click="toggleAccordion('email')">
                <h5 class="tm__dc-title">Email updates</h5>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  class="tm__dc-chevron"
                  :class="{ 'tm__dc-chevron--open': openAccordions.email }"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="m9 6 6 6-6 6"
                  />
                </svg>
              </div>
              <div v-if="openAccordions.email" class="tm__dc-content">
                <label class="tm__checkbox-label"><input
                    v-model="editForm.emailDailySummary"
                    type="checkbox"
                    class="tm__checkbox"
                  /><span>Daily summary</span></label>
                <label class="tm__checkbox-label"><input
                    v-model="editForm.emailWeeklySummary"
                    type="checkbox"
                    class="tm__checkbox"
                  /><span>Weekly summary</span></label>
              </div>
            </div>

            <!-- 2FA -->
            <div class="tm__dc">
              <div class="tm__dc-heading" @click="toggleAccordion('twofa')">
                <h5 class="tm__dc-title">Two-factor authentication (2FA)</h5>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  class="tm__dc-chevron"
                  :class="{ 'tm__dc-chevron--open': openAccordions.twofa }"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="m9 6 6 6-6 6"
                  />
                </svg>
              </div>
              <div v-if="openAccordions.twofa" class="tm__dc-content">
                <p class="tm__section-desc">
                  2FA makes it harder for someone else to sign in to this
                  account.
                </p>
                <button class="tm__btn tm__btn--secondary" type="button">
                  Enable 2FA
                </button>
                <div
                  v-if="canManageAgents"
                  style="
                    margin-top: 24px;
                    padding-top: 16px;
                    border-top: 1px solid #f0f0f1;
                  "
                >
                  <button
                    class="tm__btn tm__btn--danger tm__btn--full"
                    type="button"
                    @click="
                      agentToDelete = selectedAgent;
                      showDeleteModal = true;
                    "
                  >
                    Delete agent
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Invite Modal -->
    <Transition name="tm-overlay">
      <div
        v-if="showInviteModal"
        class="tm__modal-overlay"
        @click.self="showInviteModal = false"
      >
        <div class="tm__modal" @click.stop>
          <div class="tm__modal-head">
            <h3 class="tm__modal-title">Invite Agent</h3>
            <button
              class="tm__drawer-close"
              type="button"
              @click="showInviteModal = false"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="m6 6 12 12m0-12L6 18"
                />
              </svg>
            </button>
          </div>
          <div class="tm__modal-body">
            <div class="tm__field">
              <label class="tm__flabel">Full name</label><input
                v-model="inviteForm.name"
                type="text"
                class="tm__finput"
                placeholder="Agent name"
              />
            </div>
            <div class="tm__field">
              <label class="tm__flabel">Email</label><input
                v-model="inviteForm.email"
                type="email"
                class="tm__finput"
                placeholder="agent@example.com"
              />
            </div>
            <div class="tm__field">
              <label class="tm__flabel">Role</label>
              <select v-model="inviteForm.roleValue" class="tm__fselect">
                <option
                  v-for="r in roleOptions"
                  :key="`i-${r.value}`"
                  :value="r.value"
                >
                  {{ r.label }}
                </option>
              </select>
            </div>
          </div>
          <div class="tm__modal-foot">
            <button
              class="tm__btn tm__btn--secondary"
              type="button"
              @click="showInviteModal = false"
            >
              Cancel
            </button>
            <button
              class="tm__btn tm__btn--primary"
              type="button"
              :disabled="inviteSubmitting"
              @click="submitInvite"
            >
              {{ inviteSubmitting ? 'Inviting…' : 'Send invitation' }}
            </button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Delete Modal -->
    <Transition name="tm-overlay">
      <div
        v-if="showDeleteModal"
        class="tm__modal-overlay"
        @click.self="showDeleteModal = false"
      >
        <div class="tm__modal" @click.stop>
          <div class="tm__modal-head">
            <h3 class="tm__modal-title">Delete Agent</h3>
            <button
              class="tm__drawer-close"
              type="button"
              @click="showDeleteModal = false"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="m6 6 12 12m0-12L6 18"
                />
              </svg>
            </button>
          </div>
          <div class="tm__modal-body">
            <p class="tm__section-desc">
              Are you sure you want to delete
              <strong>{{ agentToDelete?.name }}</strong>? This action cannot be undone.
            </p>
          </div>
          <div class="tm__modal-foot">
            <button
              class="tm__btn tm__btn--secondary"
              type="button"
              @click="showDeleteModal = false"
            >
              Cancel
            </button>
            <button
              class="tm__btn tm__btn--danger"
              type="button"
              :disabled="deleteLoading"
              @click="confirmDeleteAgent"
            >
              {{ deleteLoading ? 'Deleting…' : 'Yes, delete' }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.tm {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  font-family:
    Inter,
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    sans-serif;
  font-size: 14px;
  color: #131317;
  line-height: 1.4;
  position: relative;
}
.tm__header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 16px;
  min-height: 56px;
  flex-shrink: 0;
  border-bottom: 1px solid #e5e5e6;
}
.tm__icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: #131317;
  cursor: pointer;
}
.tm__icon-btn:hover {
  background: #f2f2f3;
}
.tm__header-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  color: #000;
}
.tm__header-spacer {
  flex: 1;
}
.tm__invite-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 500;
  color: rgba(19, 19, 23, 0.72);
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 8px;
}
.tm__invite-link:hover {
  background: #f2f2f3;
}

.tm__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 16px;
  border-bottom: 1px solid #e5e5e6;
  flex-shrink: 0;
  background: #fff;
}
.tm__search {
  flex: 0 0 288px;
  max-width: 288px;
  padding: 2px 0;
}

.tm__contact-search-input {
  padding-left: 2.75rem !important;
  margin-bottom: 0 !important;
}

.tm__contact-search-input:focus,
.tm__contact-search-input:focus-visible {
  outline: none !important;
  outline-color: transparent !important;
  box-shadow: none !important;
}

.tm__contact-search-icon {
  left: 0.875rem;
  top: 35% !important;
}
.tm__toolbar-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}
.tm__agent-count {
  font-size: 12px;
  font-weight: 600;
  color: rgba(19, 19, 23, 0.6);
}

.tm__body {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}
.tm__table-wrap {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.tm__table-scroll {
  flex: 1;
  overflow: auto;
  min-height: 0;
}
.tm__section-label {
  padding: 12px 16px 8px;
  font-size: 14px;
  font-weight: 600;
  color: #131317;
  border-bottom: 1px solid #f0f0f1;
}
.tm__state-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 240px;
}
.tm__state-empty {
  font-size: 13px;
  color: rgba(19, 19, 23, 0.55);
}
.tm__spinner {
  width: 28px;
  height: 28px;
  border: 3px solid rgba(0, 102, 255, 0.15);
  border-top-color: #0066ff;
  border-radius: 50%;
  animation: tm-spin 0.7s linear infinite;
}
@keyframes tm-spin {
  to {
    transform: rotate(360deg);
  }
}

.tm__row {
  display: grid;
  grid-template-columns: 40px minmax(220px, 1.2fr) 120px minmax(260px, 1fr) 120px 90px 130px 210px 48px;
  min-width: 1280px;
  width: 100%;
}
.tm__row--head {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #fff;
  border-bottom: 1px solid #e5e5e6;
}
.tm__row--add {
  cursor: pointer;
  border-bottom: 1px solid #f0f0f1;
  background: #fff;
}
.tm__row--add:hover {
  background: #f6f6f7;
}
.tm__row--data {
  cursor: pointer;
  border-bottom: 1px solid #f0f0f1;
  background: #fff;
  transition: background 0.1s;
}
.tm__row--data:hover {
  background: #f6f6f7;
}
.tm__row--data.tm__row--selected {
  background: rgba(0, 102, 255, 0.06);
}
.tm__cell {
  display: flex;
  align-items: center;
  padding: 0 8px;
  min-height: 52px;
  white-space: nowrap;
  font-size: 13px;
  gap: 4px;
  min-width: 0;
}
.tm__cell--check {
  justify-content: center;
  padding: 0 4px;
}
.tm__cell--name {
  padding: 0 8px 0 4px;
  gap: 10px;
}
.tm__cell--metric {
  color: rgba(19, 19, 23, 0.76);
  font-variant-numeric: tabular-nums;
}
.tm__cell--activity {
  padding: 0;
  align-self: stretch;
  overflow: visible;
}
.tm__cell--more {
  justify-content: center;
  padding: 0 4px;
}
.tm__row--head .tm__cell {
  height: 38px;
  font-size: 12px;
  font-weight: 600;
  color: rgba(19, 19, 23, 0.55);
}
.tm__checkbox-placeholder {
  display: inline-flex;
  width: 16px;
  height: 16px;
  border: 1.5px solid #d5d5d6;
  border-radius: 4px;
  flex-shrink: 0;
}
.tm__queue-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 22px;
  padding: 0 8px;
  border-radius: 999px;
  border: 1px solid #e5e5e6;
  background: #f8fafc;
  color: #111827;
  font-weight: 600;
  font-size: 12px;
}

/* ── aa-row-activity: exact copy from AgentActivityReports.vue ── */
.aa-row-activity {
  width: 100%;
  padding: 8px 20px 8px 12px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 52px;
  box-sizing: border-box;
}
.aa-activity-chart {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 40px;
}
.aa-hour-ticks {
  position: relative;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex: 1 1 auto;
}
.aa-hour-tick {
  width: 1px;
  height: 4px;
  margin-bottom: 7px;
  background-color: #c9c9cd;
  flex: 0 0 auto;
}
.aa-hour-tick.is-major {
  height: 8px;
  background-color: #c9c9cd;
}
.aa-track {
  position: relative;
  width: 100%;
  height: 6px;
  background: #d1d5db;
  border-radius: 999px;
  overflow: visible;
}
.aa-track-inner {
  position: absolute;
  inset: 0;
  border-radius: inherit;
  overflow: hidden;
}
.aa-segment {
  position: absolute;
  top: 0;
  height: 6px;
  min-width: 1px;
}
.aa-segment--accepting {
  background: #06b6d4;
}
.aa-segment--not-accepting {
  background: #ca0005;
}
.aa-segment--upcoming {
  background: #d1d5db;
}
.aa-segment--logged-out {
  background: #ca0005;
  box-shadow: none;
}
.aa-legend-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 4px 10px;
  margin-top: 8px;
}
.aa-legend-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #62626d;
  font-size: 9px;
  line-height: 16px;
}
.aa-legend-item strong {
  color: #131317;
  font-weight: 600;
}
.aa-legend-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
}
.aa-legend-dot--accepting {
  background: #06b6d4;
}
.aa-legend-dot--not-accepting {
  background: #ca0005;
}
.aa-legend-dot--upcoming {
  background: #9ca3af;
}
.aa-legend-dot--logged-out {
  background: #ca0005;
  box-shadow: none;
}
.aa-segment-tooltip {
  position: absolute;
  display: none;
  z-index: 220;
  bottom: 18px;
  transform: translateX(-50%);
  min-width: 200px;
  box-shadow: rgba(0, 0, 0, 0.2) 0 5px 10px 0;
  border-radius: 4px;
  overflow: hidden;
}
.aa-segment-tooltip__time {
  padding: 7px 0 8px;
  font-size: 12px;
  background-color: #f3f4f6;
  color: #111827;
  text-align: center;
}
.aa-segment-tooltip__line {
  padding: 8px 12px 10px;
  background: #ffffff;
  color: #111827;
  text-align: center;
  font-size: 12px;
}
.aa-track:hover .aa-segment-tooltip {
  display: block;
}
.tm__add-btn {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1.5px solid #d5d5d6;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #131317;
  flex-shrink: 0;
}
.tm__add-label {
  font-size: 13px;
  color: rgba(19, 19, 23, 0.72);
}
.tm__agent-avatar-wrap {
  position: relative;
  flex-shrink: 0;
}
.tm__avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  text-transform: uppercase;
  overflow: hidden;
  background: #e5e7eb;
}
.tm__avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}
.tm__avatar--profile {
  width: 64px;
  height: 64px;
  font-size: 24px;
}
.tm__avatar-status-dot {
  position: absolute;
  bottom: -1px;
  right: -1px;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  border: 1.5px solid #fff;
}
.tm__name-col {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}
.tm__name {
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 13px;
}
.tm__you-badge {
  font-weight: 400;
  color: rgba(19, 19, 23, 0.55);
  font-size: 12px;
}
.tm__sub {
  font-size: 11px;
  color: rgba(19, 19, 23, 0.5);
  overflow: hidden;
  text-overflow: ellipsis;
}
.tm__role-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 4px;
  background: #f2f2f3;
  color: #131317;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid #e5e5e6;
}
.tm__role-badge--owner {
  background: #131317;
  color: #fff;
  border-color: #131317;
}
.tm__role-badge--hero {
  margin-bottom: 4px;
}
.tm__status-wrap {
  position: relative;
}
.tm__status-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border: none;
  background: transparent;
  border-radius: 8px;
  font-size: 13px;
  color: #131317;
  cursor: pointer;
  font-family: inherit;
  white-space: nowrap;
  transition: background 0.15s;
}
.tm__status-btn:hover {
  background: #f2f2f3;
}
.tm__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.tm__status-chevron {
  color: rgba(19, 19, 23, 0.55);
  transition: transform 0.2s;
}
.tm__status-chevron--open {
  transform: rotate(180deg);
}
.tm__status-popup {
  position: absolute;
  left: 0;
  top: calc(100% + 4px);
  z-index: 220;
  min-width: 200px;
  background: #fff;
  border-radius: 10px;
  box-shadow:
    0 4px 24px rgba(0, 0, 0, 0.12),
    0 1px 4px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}
.tm__status-list {
  list-style: none;
  margin: 0;
  padding: 6px;
}
.tm__status-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 12px;
  border: none;
  background: transparent;
  border-radius: 8px;
  font-size: 14px;
  color: #131317;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  transition: background 0.1s;
}
.tm__status-item:hover {
  background: #f6f6f7;
}
.tm__sdot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
}
.tm__sdot--green {
  background: #38c172;
}
.tm__sdot--red {
  background: #e3342f;
}
.tm__sdot--gray {
  background: #9ca3af;
}
.tm__status-item-divider {
  border-bottom: 1px solid #e5e5e6;
  margin-bottom: 4px;
  padding-bottom: 4px;
}
.tm__more-btn {
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  border-radius: 8px;
  color: rgba(19, 19, 23, 0.55);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.tm__more-btn:hover {
  background: #f2f2f3;
  color: #131317;
}

.tm__drawer-overlay {
  position: absolute;
  inset: 0;
  background: rgba(17, 24, 39, 0.16);
  z-index: 160;
  display: flex;
  justify-content: flex-end;
}
.tm__drawer {
  width: 410px;
  max-width: 100%;
  background: #fff;
  border-left: 1px solid #e5e5e6;
  display: flex;
  flex-direction: column;
  box-shadow: -4px 0 16px rgba(0, 0, 0, 0.08);
}
.tm-drawer-overlay-enter-active,
.tm-drawer-overlay-leave-active {
  transition: opacity 0.18s ease;
}
.tm-drawer-overlay-enter-from,
.tm-drawer-overlay-leave-to {
  opacity: 0;
}
.tm-drawer-overlay-enter-active .tm__drawer,
.tm-drawer-overlay-leave-active .tm__drawer {
  transition: transform 0.22s cubic-bezier(0.4, 0, 0.2, 1);
}
.tm-drawer-overlay-enter-from .tm__drawer,
.tm-drawer-overlay-leave-to .tm__drawer {
  transform: translateX(100%);
}
.tm__drawer-head {
  display: flex;
  align-items: center;
  height: 56px;
  padding: 0 16px;
  border-bottom: 1px solid #e5e5e6;
  flex-shrink: 0;
  background: #fff;
  gap: 8px;
}
.tm__back-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 500;
  color: rgba(19, 19, 23, 0.72);
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 8px;
  font-family: inherit;
}
.tm__back-btn:hover {
  background: #f2f2f3;
}
.tm__drawer-title {
  flex: 1;
  font-size: 18px;
  font-weight: 600;
  color: #131317;
  text-align: center;
}
.tm__drawer-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: rgba(19, 19, 23, 0.6);
  cursor: pointer;
  border-radius: 8px;
}
.tm__drawer-close:hover {
  background: #f2f2f3;
  color: #131317;
}
.tm__drawer-body {
  flex: 1;
  overflow-y: auto;
  background: #f0f0f1;
}

.tm__profile-hero {
  background: #fff;
  padding: 24px 20px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  border-bottom: 1px solid #e5e5e6;
}
.tm__profile-avatar-wrap {
  position: relative;
}
.tm__profile-status-dot {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid #fff;
}
.tm__profile-name-col {
  text-align: center;
}
.tm__profile-name {
  font-size: 18px;
  font-weight: 700;
  color: #131317;
  margin: 4px 0 0;
}
.tm__profile-sub {
  font-size: 13px;
  color: rgba(19, 19, 23, 0.55);
  margin: 2px 0 0;
}

.tm__dc {
  position: relative;
  background: #fff;
  width: 100%;
  color: #131317;
}
.tm__dc--divider {
  border-bottom: 1px solid #e5e5e6;
}
.tm__dc-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  cursor: pointer;
  user-select: none;
}
.tm__dc-heading:hover {
  background: #f9f9f9;
}
.tm__dc-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  color: #000;
}
.tm__dc-chevron {
  color: rgba(19, 19, 23, 0.55);
  transition: transform 0.2s ease;
}
.tm__dc-chevron--open {
  transform: rotate(90deg);
}
.tm__dc-content {
  padding: 0 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.tm__dc-empty-note {
  font-size: 13px;
  color: rgba(19, 19, 23, 0.45);
}

/* Working hours schedule */
.tm__wh-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  color: rgba(19, 19, 23, 0.72);
}
.tm__wh-tz {
  display: flex;
  align-items: center;
  gap: 6px;
}
.tm__wh-total {
  font-weight: 600;
  color: #131317;
}
.tm__wh-days {
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid #e5e5e6;
  border-radius: 10px;
  overflow: hidden;
}
.tm__wh-day {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-bottom: 1px solid #f0f0f1;
  background: #fafafa;
}
.tm__wh-day:last-child {
  border-bottom: none;
}
.tm__wh-day--active {
  background: #fff;
}
.tm__wh-day-check {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
}
.tm__wh-day-label {
  font-size: 14px;
  font-weight: 500;
  color: #131317;
  flex: 1;
}
.tm__wh-day-range {
  font-size: 13px;
  color: rgba(19, 19, 23, 0.55);
}

/* Form fields */
.tm__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.tm__flabel {
  font-size: 13px;
  font-weight: 600;
  color: #000;
}
.tm__flabel-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.tm__finput {
  height: 40px;
  padding: 0 12px;
  border: 1px solid #d5d5d6;
  border-radius: 12px;
  background: #f3f4f6;
  font-size: 14px;
  color: #000;
  outline: none;
  font-family: inherit;
  width: 100%;
}
.tm__finput:focus {
  border-color: #0066ff;
  box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.15);
  background: #fff;
}
.tm__finput--locked {
  background: #f3f4f6;
  color: #000;
  cursor: not-allowed;
}
.tm__finput-wrap {
  position: relative;
  display: flex;
  align-items: center;
}
.tm__flock {
  position: absolute;
  right: 10px;
  color: rgba(19, 19, 23, 0.45);
  pointer-events: none;
}
.tm__fselect {
  height: 40px;
  padding: 0 28px 0 12px;
  border: 1px solid #d5d5d6;
  border-radius: 12px;
  background: #f3f4f6
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24'%3E%3Cpath stroke='%23666' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m6 9 6 6 6-6'/%3E%3C/svg%3E")
    no-repeat right 6px center / 14px;
  font-size: 14px;
  color: #000;
  outline: none;
  cursor: pointer;
  appearance: none;
  font-family: inherit;
  width: 100%;
}
.tm__fselect:focus {
  border-color: #0066ff;
  box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.15);
  background: #fff;
}
.tm__fhint {
  font-size: 12px;
  color: rgba(19, 19, 23, 0.55);
  margin: 0;
}
.tm__change-pw {
  font-size: 13px;
  color: #0059e1;
  background: transparent;
  border: none;
  cursor: pointer;
  text-decoration: none;
  padding: 0;
  font-family: inherit;
}
.tm__change-pw:hover {
  text-decoration: underline;
}

.tm__clwrap {
  display: flex;
  align-items: center;
}
.tm__clgroup {
  display: grid;
  grid-template-columns: 64px 30px;
  height: 48px;
  border: 1px solid #d5d5d6;
  border-radius: 14px;
  overflow: hidden;
  background: #f3f4f6;
}
.tm__clinput {
  width: 100%;
  height: 100%;
  border: none;
  text-align: center;
  font-size: 28px;
  color: #000;
  font-family: inherit;
  outline: none;
  background: transparent;
  appearance: textfield;
}
.tm__clinput::-webkit-inner-spin-button,
.tm__clinput::-webkit-outer-spin-button {
  -webkit-appearance: none;
}
.tm__clarrows {
  display: grid;
  grid-template-rows: 1fr 1fr;
  border-left: 1px solid #d5d5d6;
  background: #fff;
}
.tm__clarrow {
  border: none;
  background: transparent;
  font-size: 11px;
  cursor: pointer;
  color: rgba(19, 19, 23, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}
.tm__clarrow:hover {
  background: #f6f6f7;
  color: #131317;
}
.tm__clarrow + .tm__clarrow {
  border-top: 1px solid #d5d5d6;
}

.tm__radio-label {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 14px;
  color: #131317;
  cursor: pointer;
}
.tm__radio-label--multiline {
  align-items: flex-start;
}
.tm__radio {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  margin-top: 3px;
  accent-color: #0066ff;
  cursor: pointer;
}
.tm__radio-sub {
  display: block;
  font-size: 12px;
  color: rgba(19, 19, 23, 0.55);
  margin-top: 2px;
}
.tm__checkbox-label {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 14px;
  color: #131317;
  cursor: pointer;
}
.tm__checkbox {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  margin-top: 2px;
  accent-color: #0066ff;
  cursor: pointer;
}
.tm__section-desc {
  font-size: 13px;
  color: rgba(19, 19, 23, 0.72);
  margin: 0;
  line-height: 1.5;
}

.tm__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border-radius: 10px;
  border: 1px solid transparent;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s,
    color 0.15s;
  text-decoration: none;
  white-space: nowrap;
  height: 36px;
  padding: 0 16px;
}
.tm__btn--primary {
  background: #0066ff;
  color: #fff;
  border-color: #0066ff;
}
.tm__btn--primary:hover:not(:disabled) {
  background: #0058d8;
  border-color: #0058d8;
}
.tm__btn--secondary {
  background: #fff;
  color: #131317;
  border-color: #d5d5d6;
}
.tm__btn--secondary:hover:not(:disabled) {
  background: #f2f2f3;
}
.tm__btn--danger {
  background: #fff;
  color: #b42318;
  border-color: rgba(180, 35, 24, 0.35);
}
.tm__btn--danger:hover:not(:disabled) {
  background: rgba(180, 35, 24, 0.06);
}
.tm__btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.tm__btn--full {
  width: 100%;
}

.tm__modal-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 240;
}
.tm__modal {
  background: #fff;
  border-radius: 16px;
  width: 480px;
  max-width: calc(100% - 32px);
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
}
.tm__modal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e5e6;
}
.tm__modal-title {
  font-size: 17px;
  font-weight: 700;
  margin: 0;
}
.tm__modal-body {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.tm__modal-foot {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 20px 20px;
}

.tm-popup-enter-active,
.tm-popup-leave-active {
  transition:
    opacity 0.15s,
    transform 0.15s;
}
.tm-popup-enter-from,
.tm-popup-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
.tm-overlay-enter-active,
.tm-overlay-leave-active {
  transition: opacity 0.2s;
}
.tm-overlay-enter-from,
.tm-overlay-leave-to {
  opacity: 0;
}

@media (max-width: 768px) {
  .tm {
    border-radius: 0;
    border: 0;
  }

  .tm__drawer {
    width: 100%;
  }
  .tm__toolbar {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    padding: 12px 16px 16px;
  }
  .tm__search {
    flex: 1 1 0%;
    max-width: none;
    padding: 0;
  }

  .tm__toolbar-actions {
    justify-content: flex-start;
    gap: 10px;
    flex-wrap: wrap;
  }

  .tm__agent-count {
    font-size: 14px;
    font-weight: 700;
  }

  .tm__btn--primary {
    min-height: 36px;
    padding: 0 16px;
    border-radius: 10px;
  }
}

::global(body.dark) .tm,
::global(body.dark) .tm__header,
::global(body.dark) .tm__toolbar,
::global(body.dark) .tm__table-wrap,
::global(body.dark) .tm__table-scroll,
::global(body.dark) .tm__row--head,
::global(body.dark) .tm__row--add,
::global(body.dark) .tm__row--data,
::global(body.dark) .tm__drawer,
::global(body.dark) .tm__drawer-head,
::global(body.dark) .tm__dc,
::global(body.dark) .tm__modal,
::global(body.theme-mix) .tm,
::global(body.theme-mix) .tm__header,
::global(body.theme-mix) .tm__toolbar,
::global(body.theme-mix) .tm__table-wrap,
::global(body.theme-mix) .tm__table-scroll,
::global(body.theme-mix) .tm__row--head,
::global(body.theme-mix) .tm__row--add,
::global(body.theme-mix) .tm__row--data,
::global(body.theme-mix) .tm__drawer,
::global(body.theme-mix) .tm__drawer-head,
::global(body.theme-mix) .tm__dc,
::global(body.theme-mix) .tm__modal {
  background: #fff !important;
  color: #131317 !important;
}

::global(body.dark) .tm__header-title,
::global(body.dark) .tm__row--head .tm__cell,
::global(body.dark) .tm__section-label,
::global(body.dark) .tm__dc-title,
::global(body.dark) .tm__flabel,
::global(body.dark) .tm__finput,
::global(body.dark) .tm__fselect,
::global(body.dark) .tm__name,
::global(body.dark) .tm__sub,
::global(body.dark) .tm__cell,
::global(body.dark) .tm__role-badge,
::global(body.dark) .tm__status-btn,
::global(body.dark) .tm__profile-name,
::global(body.dark) .tm__profile-sub,
::global(body.theme-mix) .tm__header-title,
::global(body.theme-mix) .tm__row--head .tm__cell,
::global(body.theme-mix) .tm__section-label,
::global(body.theme-mix) .tm__dc-title,
::global(body.theme-mix) .tm__flabel,
::global(body.theme-mix) .tm__finput,
::global(body.theme-mix) .tm__fselect,
::global(body.theme-mix) .tm__name,
::global(body.theme-mix) .tm__sub,
::global(body.theme-mix) .tm__cell,
::global(body.theme-mix) .tm__role-badge,
::global(body.theme-mix) .tm__status-btn,
::global(body.theme-mix) .tm__profile-name,
::global(body.theme-mix) .tm__profile-sub {
  color: #131317 !important;
  -webkit-text-fill-color: #131317 !important;
  text-shadow: none !important;
}
</style>
